import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useResizeDetector } from 'react-resize-detector'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import DriveFolderUploadOutlinedIcon from '@mui/icons-material/DriveFolderUploadOutlined'

import colors from '@/assets/scss/_themes-vars.module.scss'
import CircularProgress from '@mui/material/CircularProgress'

/**
 * @param onUpload - Callback function to handle file upload
 * @param accept - Accepted file types
 * @param loading - Loading state
 * @param disabled - Disabled state
 */
export type UploadButtonProps = {
  onUpload?: (file: File) => void
  accept?: string
  loading?: boolean
  disabled?: boolean
}

export function UploadButton(props: UploadButtonProps) {
  const { onUpload, accept, loading, disabled } = props
  const { t } = useTranslation()

  const inputRef = useRef<HTMLInputElement>()
  const textRef = useRef<HTMLParagraphElement>(null)

  const { ref: buttonRef, width: buttonWidth } = useResizeDetector()

  const handleOpenUploadPopup = () => {
    inputRef.current?.click()
  }

  const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) onUpload?.(file)
  }

  return (
    <Button
      onClick={handleOpenUploadPopup}
      ref={buttonRef}
      disabled={disabled}
      sx={{
        cursor: loading || disabled ? 'not-allowed' : 'pointer',
        width: '100%',
        position: 'relative',
        border: `1px dashed ${colors.gray300}`,
        borderRadius: '8px',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <Box component="div" sx={{ pt: '70%', position: 'relative' }}>
        <Stack
          alignItems="center"
          justifyContent="center"
          spacing={1}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <Box
            component="input"
            data-testid="upload-input"
            ref={inputRef}
            onChange={handleUpload}
            type="file"
            sx={{ display: 'none' }}
            accept={accept}
            disabled={loading || disabled}
          />
          {loading ? (
            <CircularProgress size={24} />
          ) : (
            <>
              <DriveFolderUploadOutlinedIcon
                htmlColor={colors.gray400}
                sx={[
                  (buttonWidth ?? 0) < (textRef.current?.getBoundingClientRect().width ?? 0) && {
                    margin: '0 !important',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                  },
                ]}
              />
              <Typography
                ref={textRef}
                textAlign="center"
                variant="button"
                component="p"
                sx={[
                  { fontSize: '8px', fontWeight: 500, whiteSpace: 'nowrap' },
                  (buttonWidth ?? 0) < (textRef.current?.getBoundingClientRect().width ?? 0) && { opacity: 0 },
                ]}
              >
                {t('component.ui.uploadButton.addDocumentButton.label')}
              </Typography>
            </>
          )}
        </Stack>
      </Box>
    </Button>
  )
}

export default UploadButton
